<template>
    <b-row class="m-0 h-100">
        <b-col class="h-100 flex-grow-1 d-flex flex-column p-0">
            <b-card header-class="text-center" body-class="d-flex flex-column p-0" class="h-100 bg-white">
                <b-card-title v-if="handleMask" class="text-center mb-0" title-tag="div">
                    <!-- Gestion des masques de configuration  -->
                    <b-row class="p-0 m-0" v-if="selectedFeature">
                        <Masks :FeatureName="selectedFeature" :SelectedMask.sync="selectedMask" :Reload.sync="reloadMasks" :Variant="Variant" @SaveMasksEvent="saveMask" v-on="$listeners" />
                    </b-row>
                </b-card-title>
                <!-- Composant de Config pour paramétrage de la fonction -->
                <template v-if="selectedFeature">
                    <component :ref="selectedFeature" v-bind:is="selectedFeature" :Configs.sync="configs" :SelectedMask="selectedMask" :ShowOverlay.sync="showOverlay"
                    :Errors.sync="errorList" :Variant="Variant" @HandleMaskEvent="handleMask = $event" class="flex-grow-1 w-100 overflow-auto no-gutters p-0" />

                    <!-- Overlay de blocage du panneau de configuration lors du chargement des résultats -->
                    <b-overlay no-wrap :show="Loading" :opacity="0.2" variant="gray-light" >
                        <template #overlay>
                            <div class="m-0 pt-1 h-100 text-center d-flex align-items-center justify-content-center"/>
                        </template>
                    </b-overlay>

                    <!-- Overlay d'information lorsqu'aucune synthèse n'est disponible -->
                    <b-overlay no-wrap no-center rounded :show="showOverlay" variant="gray-light" >
                        <template #overlay>
                             <!-- 'Overlay.NoData.Title -> "Aucune synthèse disponible." -->
                             <!-- 'Overlay.NoData.Message'-> "Veuillez intégrer des données afin de poursuivre vos analyses" -->
                            <div class="m-0 pt-1 h-100 text-center d-flex align-items-center justify-content-center">
                                {{ $t('Overlay.NoData.Title') }}<br/>  {{ $t('Overlay.NoData.Message') }}
                            </div>
                        </template>
                    </b-overlay>
                    
                    <!-- La condition 'configs.Data[0].Synthesis == 0' correspond à sa valeur avant affectation d'une valeur d'énumération -->
                    <b-button @click="loadWorkResult()" size="md" :variant="Variant" :disabled="Loading || (configs.Data && configs.Data.length > 0 && ('Synthesis' in configs.Data[0]) && configs.Data[0].Synthesis == 0)" pill>
                         <!-- Refresh' -> "Actualiser" -->
                         {{ $t('Refresh') }}
                    </b-button>
                </template>
                <!-- Choix d'une fonction Analysis -->
                <template v-else>
                    <b-row v-for="feature in features" :key="feature.Name" class="px-3 pb-2">
                        <b-button @click="selectFeature(feature.Name, feature.SelectMode)" pill size="md" class="w-100" :variant="'outline-' + Variant">
                            {{ $t(feature.Name) }}
                        </b-button>
                    </b-row>
                </template>
            </b-card>
        </b-col>

        <!-- Toaster affichant la liste des erreurs -->
        <b-toast :id="$id('featureValidation')" variant="danger" solid auto-hide-delay="5000" toaster="b-toaster-top-center">
            <template #toast-title>
                <b-icon icon="exclamation-circle" class="h4 mt-2 mr-2" />
                 <!-- Errors -> "Erreurs" -->
                <b>{{ $t('Errors') }}</b>
            </template>
            <b-list-group>
                <b-list-group-item v-for="(error, index) in errorList" :key="index" variant="danger" style="cursor: pointer;">
                    {{ error.Message }}
                </b-list-group-item>
            </b-list-group>
        </b-toast>
    </b-row>
</template>

<script lang="ts">
    import { Component, Prop, InjectReactive, PropSync, Vue, Watch } from "vue-property-decorator";
    import axios from "axios";
    import Dictionary from "@/types/Dictionary";
    import { API_URL } from "@/App.settings";
    
    import SelectModeEnum from "@/enums/SelectModeEnum";

    /** Gestions des pins */
    import Pin from '@/types/FilterTree/Pin';
    /** Gestion des masques de configuration */
    import Masks from '@/components/commons/Config/Masks.vue';
    import Mask from "@/types/Config/Mask";
    import Feature from "@/types/Feature";

    /** Liste des features Analysis configurables */
    import Benchmarking from '@/components/commons/Config/Benchmarking.vue';
	import Evolution from '@/components/commons/Config/Evolution.vue';
	import UnitCost from '@/components/commons/Config/UnitCost.vue';
    import Goal from '@/components/commons/Config/Goal.vue';
    import OfficialLabels from '@/components/commons/Config/OfficialLabels.vue';
    import  locales  from "./locales/Config.json"

    /** 
     * Composant de sélection d'une fonction Analysis puis de chargement de configuration/paramètres liés à cette fonction Analysis
     * + gestion des masques de configuration
     */
    @Component({
        components: {
            Masks,
            /** Liste des features Analysis configurables */
            Benchmarking,
            Evolution,
            Goal,
            UnitCost,
            OfficialLabels
        },
        i18n:{messages : locales}
    })
    export default class Config extends Vue {
		/** Variant de couleur à appliquer sur le composant */
		@Prop({ default: () => "Primary" })
		public Variant!: string;

        /** Données synchronisées pour le composant appelant */
        @PropSync('Configs', { default: () => [{}] })
        public configs!: Dictionary<any>;

        /** Composant Vue.js cible de la feature à charger  */
        @Prop()
        public Target!: string;

        /** Indique si le composant Work doit afficher un overlay de chargement ou non */
        @Prop()
        public Loading!: boolean;

        /** Indique si l'on utilise des données fictives ou non */
        @Prop()
        public Preview!: boolean;

        /** Permet l'affichage de l'overlay sur le panneau de configuration */
        private showOverlay : boolean = false;

        /** Indique si le composant Mask doit apparaitre ou non */
        private handleMask: boolean = true;

        /** Nom de l'instance d'arborescence à charger */
        @InjectReactive()
        protected filterTreeStore;

        //#region Gestion des fonctions

        /** Nom des fonctions Analysis disponibles */
        @Prop({ default: () => [] })
        public AvailableFeatures!: string[];

        /** Liste des features accessibles sur un widget */
        private features!: Feature[];

        /** Par défaut, sélectionne la première feature s'il n'y en a qu'une seule de disponible */
        @Watch('AvailableFeatures', { immediate: true })
        private selectFirstFeature() {
            console.debug('Config - selectFirstFeature() - AvailableFeatures : ', this.AvailableFeatures);
            
            /** @todo Ne récupère pour le moment que les fonctions Analysis. A voir évolution si nécessité de récupérer des fonctions non Analysis ==> nécessitera de faire évoluer l'attribut WidgetLibrary avec la propriété targetFeature côté server pour distinguer les fonctions de même noms avec des parents différents (exemple : Goal) */
            let availableFeaturesPaths: string[] = this.AvailableFeatures.map(x => 'Analysis \/ [A-Za-z]* \/ ' + x);

            this.features = this.$store.getters['GetFilteredFeatures'](availableFeaturesPaths, this.AvailableFeatures);

            /** S'il n'y a qu'une seule feature alors on la choisie par défaut */
            if (this.features && this.features.length == 1)
                this.selectFeature(this.features[0].Name, this.features[0].SelectMode);
        }

        /** Fonction sélectionnée */
        private selectedFeature: string = "";

        /** 
         * Sélection d'une fonction dans le panneau de droite
         * @event SelectedFeatureEvent Renvoi un objet avec le nom de la fonction sélectionnée et son mode de sélection
         */
        private selectFeature(feature: string, selectMode: SelectModeEnum) {
            this.selectMode = selectMode;

            console.debug("Config - selectFeature() - selectMode : " + selectMode + " - this.selectMode :" + this.selectMode);
            
            this.$emit("SelectedFeatureEvent", feature, selectMode);
        }

        /** Mode de sélection utilisé (mono ou multi-noeuds) */
        @PropSync('SelectMode')
        public selectMode!: SelectModeEnum;

        /** 
         * Pointeur vers fonction pour désactiver watcher sur l'instance de FilterTree () sur le noeud ou les pins sélectionnés 
         * @see https://vuejs.org/guide/essentials/watchers#stopping-a-watcher
         **/
        private unwatchFilterTree = undefined;
        private unwatchIsHistoricMode = undefined;

        @Watch('configs.Feature', { immediate: true })
        private async updateSelectedFeature() {
            console.debug("Config - updateSelectedFeature() - configs : %O - SelectMode : %O", this.configs, this.selectMode);

            if (this.configs && 'Feature' in this.configs) {
                // Mise à jour de la fonction sélectionnée pour chargement du composant associé
                this.selectedFeature = this.configs['Feature'];
                this.selectedMask = {} as Mask;

                /** Mise à jour de la config en fonction de la sélection dans l'arborescence */
                if (!this.Preview) {
                    /** Désactive le watcher sur l'instance de FilterTree () sur le noeud ou les pins sélectionnés 
                     * @see https://vuejs.org/guide/essentials/watchers#stopping-a-watcher */
                    // if (this.unwatchFilterTree)
                    //     this.unwatchFilterTree();

                    /** Déclare un watcher pour mise-à-jour des Pins de configs à partir de ce qui est sélectionné dans l'instance de l'arborescence */
                    this.unwatchFilterTree = this.$store.watch((state, getters) => getters[this.filterTreeStore + '/GetPins'], (val: Pin[], oldValue: Pin[]) => {
                        console.debug("Config - updateSelectedFeature() - case SelectModeEnum.Multi - val : ", val);
                        if (val)
                            this.configs["Pins"] = val;

                        // console.debug('Config - updateSelectedFeature - typeof (this.$refs[this.selectedFeature] as any).ProcessPin : ', typeof (this.$refs[this.selectedFeature] as any).ProcessPin);
                        
                        // Chaque fonction traite les Pins spécifiquement dans le ProcessPin()
                        if (this.selectedFeature && this.selectedFeature in this.$refs && this.$refs[this.selectedFeature] && typeof (this.$refs[this.selectedFeature] as any).ProcessPin === "function")
                            (this.$refs[this.selectedFeature] as any).ProcessPin();
                    }, { immediate: true });


                    /** Désactive le watcher sur l'instance de FilterTree () sur le noeud ou les pins sélectionnés 
                     * @see https://vuejs.org/guide/essentials/watchers#stopping-a-watcher */
                    // if (this.unwatchIsHistoricMode)
                    //     this.unwatchIsHistoricMode();

                    // Mise à jour Mode historique
                    this.unwatchIsHistoricMode = this.$store.watch((state, getters) => getters[this.filterTreeStore + '/GetHistoricMode'], (val: boolean, oldValue: boolean) => {
                        if (val)
                            Vue.set(this.configs, "IsHistoricMode", val);
                    }, { immediate: true });
                }
            }
        }
        //#endregion Gestion des fonctions

        //#region Gestion des masques

        /** Masque sélectionné */
        private selectedMask: Mask = {} as Mask;

        /** Indique si la liste des masques doit être rechargées */
        private reloadMasks: boolean = false;

        /**
         * Sauvegarde masque
         * @param maskName Nom du masque
         */
        private async saveMask(maskName: string) {
            //Arguments
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

            console.debug("Config - saveMask : ", this.selectedMask);

            // Sur ajout d'un masque
            if (Object.keys(this.selectedMask).length == 0) {
                args["Id"] = 0;
            }
            else {  // Sur-mise-à jour d'un masque
                args["Id"] = this.selectedMask.Id ;
            }
            args["Name"] = maskName;
            args["Parameters"] = (this.$refs[this.selectedFeature] as any).SaveMask();

            console.debug("Config - saveMask - args : ", args);

            await axios.post(API_URL + "Feature/" + this.selectedFeature + "/SaveMask", args).then(async (response) => {
                if (response && response.data){
                    // permet de déclencher le rechargement de la liste des masque
                    this.reloadMasks = true;
                    this.selectedMask = response.data;
                    
                }
            });
        }
        //#endregion Gestion des masques
        /** Permet de générer le titre de l'analysis (type de synthèse - données | période(s)) */
        public GenerateTitle() {
            if(this.$refs[this.selectedFeature]) {
                // Créer un GetTitle pour chaque feature n'ayant qu'une seul "bloc" de données ?
                return (this.$refs[this.selectedFeature] as any).GenerateTitle();
            }
        }

        //#region Validation

        /** Liste d'erreur de validation */
        @PropSync('Errors', {default: () => []})
        public errorList!: { Message: string }[];

        /** Permet de checker l'invalidité de la configuration de la feature sélectionnée*/
        public CheckInvalidity(): boolean {
            if(this.$refs[this.selectedFeature]){
                return (this.$refs[this.selectedFeature] as any).CheckInvalidity();
            }
        }

        //#endregion Validation

        /** Initie le chargement du résultat du composant Work */
        private loadWorkResult() {
            this.$bvToast.hide(this.uid + '-featureValidation');

            // vide la liste des erreurs
            this.errorList.splice(0);

            /** Vérification que des pins existent avant traitement */
            if (!this.Preview && this.configs && 'Pins' in this.configs && this.configs["Pins"].length == 0) {
                // Validation.NoPinsSelected -> "Veuillez cocher des éléments de patrimoine dans l'arborescence."
                this.errorList.push({ Message: this.$t('Validation.NoPinsSelected').toString() });
            }

            // Verification des champs dans la feature sélectionnée
            if ((this.$refs[this.selectedFeature] as any).CheckInvalidity() || this.errorList.length > 0)
                this.$bvToast.show(this.uid + '-featureValidation');
            else {
                /** Notifie le composant parent pour déclencher un loadWorkResult */
                this.$emit('LoadWorkResultEvent');
            }
        }

        beforeDestroy() {
            // Libération du watcher
            if (this.unwatchFilterTree)
                this.unwatchFilterTree();

            if (this.unwatchIsHistoricMode)
                this.unwatchIsHistoricMode();
        }
    }
</script>

<style scoped>
</style>